import { CboCuidadoCompartilhadoSelectModel } from 'components/form/field/select/CboCuidadoCompartilhadoSelectField/CboCuidadoCompartilhadoSelectField'
import { CiapSelectFieldModel } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { LotacaoAndEstagioSelectFieldModel } from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { FlagsContextModel } from 'config/useFlagsContext'
import { Calculation } from 'final-form-calculate'
import { UnidadeSaude } from 'graphql/types.generated'
import { isEmpty, isUndefinedOrNull } from 'util/checks'
import { MetaRoot } from 'util/metaPath'

import { CuidadoCompartilhadoPlanoModel, TipoCompartilhamentoCuidadoPlanoEnum } from '../model-cuidadocompartilhado'
import { getIsVinculacaoCuidadoAtiva } from './utils-cuidadoCompartilhado'

export const cuidadoCompartilhadoFormCalculator = (
  meta: MetaRoot<CuidadoCompartilhadoPlanoModel>,
  isSomenteCiap: boolean,
  isEdit: boolean,
  isAtendimentoOdonto: boolean,
  flags: FlagsContextModel
): Calculation[] => {
  const isVinculacaoCuidadoAtiva = getIsVinculacaoCuidadoAtiva(flags, isAtendimentoOdonto)

  if (isVinculacaoCuidadoAtiva) {
    return [
      {
        field: meta.cbo.absolutePath(),
        updates: {
          [meta.unidadeSaude.absolutePath()]: (
            value: CboCuidadoCompartilhadoSelectModel,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (estahAdicionandoOuEditandoSegundaVezCbo(isEdit, prevValues) && isCboDifferent(value, prevValues))
              return null
            return allValues.unidadeSaude
          },
          [meta.lotacao.absolutePath()]: (
            value: CboCuidadoCompartilhadoSelectModel,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (estahAdicionandoOuEditandoSegundaVezCbo(isEdit, prevValues) && isCboDifferent(value, prevValues))
              return null
            return allValues.lotacao
          },
          [meta.cidadaoAceitaAtendTic.absolutePath()]: (
            value: CboCuidadoCompartilhadoSelectModel,
            allValues: CuidadoCompartilhadoPlanoModel,
            _: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (value.isGuia) return null
            return allValues.cidadaoAceitaAtendTic
          },
          [meta.problemasECondicoes.absolutePath()]: (
            value: CboCuidadoCompartilhadoSelectModel,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (estahAdicionandoOuEditandoSegundaVezCbo(isEdit, prevValues)) {
              if (prevValues.tipo === TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO && isSomenteCiap)
                return { ciap: allValues.problemasECondicoes?.ciap, cid10: allValues.problemasECondicoes?.cid10 }

              const virouCuidadoCompartilhado = prevValues.cbo?.isGuia && value.isCuidado
              if (isSomenteCiap && virouCuidadoCompartilhado)
                return { ciap: allValues.problemasECondicoes?.ciap, cid10: null }

              const virouGuiaEncaminhamento = !!prevValues.cbo?.isCuidado && value.isGuia
              if (!isSomenteCiap && virouGuiaEncaminhamento)
                return { ciap: null, cid10: allValues.problemasECondicoes?.cid10 }
            }
            return allValues.problemasECondicoes
          },
          [meta.tipo.absolutePath()]: (
            value: CboCuidadoCompartilhadoSelectModel,
            allValues: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null

            if (isEdit && value.isCuidado) {
              if (allValues.problemasECondicoes === null) {
                return TipoCompartilhamentoCuidadoPlanoEnum.CUIDADO_COMPARTILHADO
              }
              return allValues.tipo
            }

            return value.isCuidado
              ? TipoCompartilhamentoCuidadoPlanoEnum.CUIDADO_COMPARTILHADO
              : TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO
          },
          [meta.especialidade.absolutePath()]: (
            value: CboCuidadoCompartilhadoSelectModel,
            allValues: CuidadoCompartilhadoPlanoModel,
            _: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (!value.isGuia) return null
            return allValues.especialidade
          },
        },
      },
      {
        field: meta.unidadeSaude.absolutePath(),
        updates: {
          [meta.lotacao.absolutePath()]: (
            value: UnidadeSaude,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            return isUndefinedOrNull(value) ||
              (!isEmpty(prevValues) && value?.id !== prevValues.lotacao?.unidadeSaude?.id)
              ? null
              : allValues.lotacao
          },
        },
      },
      {
        field: meta.problemasECondicoes.ciap.absolutePath(),
        updates: {
          [meta.problemasECondicoes.cid10.absolutePath()]: (
            value: CiapSelectFieldModel,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            const isGuiaEncaminhamento =
              allValues?.cbo && allValues?.tipo === TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO

            return isSomenteCiap &&
              isGuiaEncaminhamento &&
              (isUndefinedOrNull(value) ||
                (!isEmpty(prevValues) && value?.id !== prevValues.problemasECondicoes?.ciap?.id))
              ? null
              : allValues.problemasECondicoes?.cid10
          },
        },
      },
      {
        field: meta.tipo.absolutePath(),
        updates: {
          [meta.lotacao.absolutePath()]: (
            value: TipoCompartilhamentoCuidadoPlanoEnum,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (estahAdicionandoOuEditandoSegundaVezCbo(isEdit, prevValues)) return null
            if (isEmpty(prevValues)) return allValues.lotacao

            return resolveCamposNovaAba(value, allValues?.lotacao, prevValues?.lotacao)
          },
          [meta.unidadeSaude.absolutePath()]: (
            value: TipoCompartilhamentoCuidadoPlanoEnum,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (estahAdicionandoOuEditandoSegundaVezCbo(isEdit, prevValues)) return null

            return resolveCamposNovaAba(value, allValues?.unidadeSaude, prevValues?.unidadeSaude)
          },
          [meta.cidadaoAceitaAtendTic.absolutePath()]: (
            value: TipoCompartilhamentoCuidadoPlanoEnum,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (estahAdicionandoOuEditandoSegundaVezCbo(isEdit, prevValues)) return null

            return resolveCamposNovaAba(value, allValues?.cidadaoAceitaAtendTic, prevValues?.cidadaoAceitaAtendTic)
          },
          [meta.problemasECondicoes.absolutePath()]: (
            value: TipoCompartilhamentoCuidadoPlanoEnum,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (value === TipoCompartilhamentoCuidadoPlanoEnum.CUIDADO_COMPARTILHADO && isSomenteCiap) {
              return isEmpty(prevValues)
                ? allValues.problemasECondicoes
                : {
                    ciap: allValues?.problemasECondicoes?.ciap ?? prevValues?.problemasECondicoes?.ciap,
                    cid10: null,
                  }
            } else if (value === TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO && !isSomenteCiap) {
              return isEmpty(prevValues)
                ? allValues.problemasECondicoes
                : {
                    ciap: null,
                    cid10: allValues?.problemasECondicoes?.cid10 ?? prevValues?.problemasECondicoes?.cid10,
                  }
            } else {
              return isEmpty(prevValues)
                ? allValues.problemasECondicoes
                : {
                    ciap: allValues?.problemasECondicoes?.ciap ?? prevValues?.problemasECondicoes?.ciap,
                    cid10: allValues?.problemasECondicoes?.cid10 ?? prevValues?.problemasECondicoes?.cid10,
                  }
            }
          },
          [meta.especialidade.absolutePath()]: (
            value: TipoCompartilhamentoCuidadoPlanoEnum,
            allValues: CuidadoCompartilhadoPlanoModel,
            prevValues: CuidadoCompartilhadoPlanoModel
          ) => {
            if (isUndefinedOrNull(value)) return null
            if (estahAdicionandoOuEditandoSegundaVezCbo(isEdit, prevValues)) return null

            return value === TipoCompartilhamentoCuidadoPlanoEnum.CUIDADO_COMPARTILHADO
              ? null
              : allValues?.especialidade ?? prevValues?.especialidade
          },
        },
      },
    ]
  }

  return [
    {
      field: meta.lotacao.absolutePath(),
      updates: {
        [meta.cbo.absolutePath()]: (
          value: LotacaoAndEstagioSelectFieldModel,
          allValues: CuidadoCompartilhadoPlanoModel,
          prevValues: CuidadoCompartilhadoPlanoModel
        ) => {
          if (!isUndefinedOrNull(value) && !isEmpty(prevValues))
            return { cbo: value.cbo, isGuia: true, isCuidado: true }
          return allValues.cbo
        },
      },
    },
    {
      field: meta.cbo.absolutePath(),
      updates: {
        [meta.lotacao.absolutePath()]: (
          value: CboCuidadoCompartilhadoSelectModel,
          allValues: CuidadoCompartilhadoPlanoModel,
          prevValues: CuidadoCompartilhadoPlanoModel
        ) => {
          return isUndefinedOrNull(value) || (!isEmpty(prevValues) && value?.cbo?.id !== prevValues.lotacao?.cbo?.id)
            ? null
            : allValues.lotacao
        },
      },
    },
  ]
}

const estahAdicionandoOuEditandoSegundaVezCbo = (
  isEdit: boolean,
  prevValues: CuidadoCompartilhadoPlanoModel
): boolean => !isEmpty(prevValues) && (!isEdit || !isUndefinedOrNull(prevValues.cbo?.cbo))

const isCboDifferent = (
  value: CboCuidadoCompartilhadoSelectModel,
  prevValues: CuidadoCompartilhadoPlanoModel
): boolean => value.cbo.id !== prevValues?.cbo?.cbo?.id || value.isCuidado !== prevValues?.cbo?.isCuidado

const resolveCamposNovaAba = <T>(tipo: TipoCompartilhamentoCuidadoPlanoEnum, allValue: T, prevValue: T): T | null => {
  return tipo === TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO ? null : allValue ?? prevValue
}
